import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { inject, observer } from 'mobx-react';
import { debounce } from 'lodash';
import compose from 'helpers/compose';

import makeStyles from '@material-ui/core/styles/makeStyles';
import createStyles from '@material-ui/core/styles/createStyles';
import { Theme } from '@material-ui/core/styles/createTheme';
import { Autocomplete } from '@material-ui/lab';
import { CircularProgress } from '@material-ui/core';
import Button from '@material-ui/core/Button';
import SearchIcon from '@material-ui/icons/Search';
import CloseIcon from '@material-ui/icons/Close';
import Grid from '@material-ui/core/Grid';
import TextField from '@material-ui/core/TextField';

import ConfirmDialog from 'components/ConfirmDialog/ConfirmDialog';

import { ClientSearchStore, LookupResult } from 'stores/workflowStores/clientSearchStore';
import { AccountClientSelectable } from 'stores/workflowStores/workflowBasicStore/model';
import { RootStore } from 'stores/rootStore';
import { EdwardColors, PersonType } from 'enums';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    input: {
      '& > div.MuiInputBase-root': {
        paddingRight: '8px !important',
      },
    },
    actions: {
      '& button': {
        margin: theme.spacing(0, 1),
      },
    },
  }),
);

interface Props {
  rootStore: RootStore;
  personType: PersonType;
  accountId: string;
  refresh: () => void;
  representative?: boolean;
  detailMode?: boolean;
}

const ClientSearch: React.FC<Props> = ({ personType, rootStore, accountId, refresh, representative, detailMode }) => {
  const { t } = useTranslation();
  const classes = useStyles();

  const [store] = useState(new ClientSearchStore(rootStore, accountId, personType, representative));

  const [showSearch, setShowSearch] = useState(false);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [value, setValue] = useState<LookupResult | null>(null);
  const [open, setOpen] = useState(false);
  const [id, setId] = useState<string | null>(null);
  const [overwriteWarning, setOverwriteWarning] = useState(false);

  const clientSelectable = rootStore.stores.editAccountStore.data?.selectable;

  const handleSearch = useMemo(
    () =>
      debounce(async (query: string) => {
        setLoading(true);
        await store.fetchLookupOptions(query);
        setLoading(false);
      }, 2000),
    [store],
  );

  return (
    <>
      {!showSearch ? (
        <Grid item xs={12}>
          <Button onClick={() => setShowSearch(true)} color="default" variant="contained">
            {t(`clientSearch_useExisting_${personType}${representative ? '_REP' : ''}`)}
          </Button>
        </Grid>
      ) : (
        <>
          <Grid item xs={8}>
            <Autocomplete
              open={open}
              onOpen={() => {
                setOpen(true);
              }}
              onClose={() => {
                setOpen(false);
              }}
              value={value}
              onChange={(event: any, newValue: LookupResult | null) => {
                setValue(newValue);
                setId(newValue ? newValue.id : null);
                setSearch(newValue ? newValue.displayValue : '');
              }}
              getOptionSelected={(option, value) => option.displayValue === value.displayValue}
              getOptionLabel={(option) => option.displayValue}
              options={store.lookupOptions} //
              inputValue={search}
              loading={loading}
              filterOptions={(options: LookupResult[], state: object) => options}
              clearOnBlur={false}
              noOptionsText={t(
                search.trim().length < 3
                  ? 'general_noResultsTooShort'
                  : store.searchTooBroad
                  ? 'general_noResultsTooBroad'
                  : 'general_noResults',
              )}
              loadingText={t('general_searching')}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t(`clientSearch_${personType}`)}
                  variant="outlined"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment:
                      search === '' ? null : loading ? (
                        <CircularProgress color="primary" size={20} />
                      ) : (
                        <CloseIcon onClick={() => setSearch('')} />
                      ),
                    startAdornment: <SearchIcon htmlColor={EdwardColors.GRAY_LIGHTER} />,
                  }}
                  value={search}
                  onChange={(event) => {
                    setSearch(event.target.value);
                    const query = event.target.value.trim();
                    if (query.length >= 3) {
                      setLoading(true);
                      handleSearch(query);
                    } else {
                      store.clearLookupOptions();
                    }
                  }}
                  className={classes.input}
                />
              )}
            />
          </Grid>
          <Grid item xs={4} className={classes.actions}>
            <Button
              onClick={async () => {
                if (
                  (clientSelectable === AccountClientSelectable.WARN && !representative) ||
                  (clientSelectable === AccountClientSelectable.WARN_REP && representative) ||
                  detailMode
                ) {
                  setOverwriteWarning(true);
                } else {
                  !!id && (await store.linkClientToAccount(!!representative, id, refresh));
                  setSearch('');
                  setShowSearch(false);
                }
              }}
              color="primary"
              variant="contained"
              disabled={id === null}
            >
              {t('general_use')}
            </Button>
            <Button onClick={() => setShowSearch(false)} color="default" variant="contained">
              {t('general_cancel')}
            </Button>
          </Grid>
        </>
      )}

      <Grid item xs={12} />

      <ConfirmDialog
        maxWidth="xs"
        isOpen={overwriteWarning}
        close={() => {
          setOverwriteWarning(false);
        }}
        confirm={async () => {
          !!id && (await store.linkClientToAccount(!!representative, id, refresh));
          setSearch('');
          setShowSearch(false);
          setOverwriteWarning(false);
        }}
      >
        {t(`clientSearch_overwriteWarning_${personType}${representative ? '_REP' : ''}`)}
      </ConfirmDialog>
    </>
  );
};

export default compose(inject('rootStore'), observer)(ClientSearch);
